// ===================================================
// src/components/Planning/StockShipmentTimeline.js
// ===================================================
import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const StockShipmentTimeline = ({ data, region }) => {
  if (!data || data.length === 0) {
    console.warn("⚠️ No data for region:", region);
    return null;
  }

  // 🧮 Build dataset — convert arrival dates to numeric timestamps for plotting
  const chartData = data.map((day) => {
    const dur = day.shipment?.duration_days || {};
    const baseDays = region === "US" ? dur.china_us || 0 : dur.china_uk || 0;
    const customerDays =
      region === "US" ? dur.us_customer || 0 : dur.uk_customer || 0;

    const shipDate = new Date(day.date);
    const warehouseArrival = new Date(day.date);
    warehouseArrival.setDate(warehouseArrival.getDate() + baseDays);

    const customerArrival = new Date(warehouseArrival);
    customerArrival.setDate(customerArrival.getDate() + customerDays);

    return {
      date: day.date,
      shipTimestamp: shipDate.getTime(),
      warehouseTimestamp: warehouseArrival.getTime(),
      customerTimestamp: customerArrival.getTime(),
      baseDays,
      customerDays,
    };
  });

  console.log("📊 Chart data (timestamps) for", region, chartData.slice(0, 3));

  return (
    <div
      style={{
        background: "white",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        padding: "1rem",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        marginBottom: "1.5rem",
      }}
    >
      <h3
        style={{
          marginBottom: "0.5rem",
          fontWeight: 600,
          color: region === "US" ? "#1e3a8a" : "#065f46",
        }}
      >
        {region === "US" ? "🇺🇸" : "🇪🇺"} Shipment Timeline Simulation — {region}
      </h3>

      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {/* X-axis = shipment date */}
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10 }}
            tickFormatter={(v) =>
              new Date(v).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })
            }
          />

          {/* Y-axis = timestamp converted back to readable date */}
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={(v) =>
              new Date(v).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })
            }
            label={{
              value: "Estimated Arrival",
              angle: -90,
              position: "insideLeft",
              fill: "#475569",
            }}
          />

          <Tooltip
            labelFormatter={(v) => `Ship Date: ${new Date(v).toLocaleDateString()}`}
            formatter={(value, name) => {
              return [new Date(value).toLocaleDateString("en-GB"), name];
            }}
          />

          <Legend />

          {/* 🟦 Ship Date */}
          <Line
            type="monotone"
            dataKey="shipTimestamp"
            stroke="#2563eb"
            strokeWidth={2}
            dot={false}
            name="Ship Date"
          />

          {/* 🟩 Warehouse Arrival */}
          <Line
            type="monotone"
            dataKey="warehouseTimestamp"
            stroke="#16a34a"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Warehouse Arrival"
          />

          {/* 🟨 Customer Arrival */}
          <Line
            type="monotone"
            dataKey="customerTimestamp"
            stroke="#facc15"
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            name="Customer Arrival"
          />
        </LineChart>
      </ResponsiveContainer>

      <div
        style={{
          marginTop: "0.75rem",
          textAlign: "center",
          fontSize: "0.9rem",
          color: "#374151",
          fontWeight: 500,
        }}
      >
        📦 Each line shows shipment, warehouse arrival, and customer delivery
      </div>
    </div>
  );
};

export default StockShipmentTimeline;
